import { useState, useMemo, useCallback, useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Cursor, getClients, getMessages, GetMessagesResp, sendMessage as sendMessageApi } from '../services/whatsappService';
import { subscribeToEvent, unsubscribeFromEvent } from '../lib/websocketService';
import type { Contact, Message } from '../types/chat';
import type { Client } from '../types/client';
import { useUserSubscriptions } from '../services/subscriptionService';
import type { Subscription } from '../services/subscriptionService';
// Assuming Message type from whatsappService is compatible or we map it.
// For now, let's assume the Message type from '../types/chat' is the target structure.

// Define a more specific API message type if possible, for now using 'any' and checking fields
interface ApiMessage { // This should mirror the structure from whatsappService.getMessages (i.e., types/whatsapp.ts Message)
  id: number; // Changed from string to number to match whatsapp.ts Message
  fromMe: boolean;
  type: string; // e.g., 'chat', 'text', 'image', 'document', etc.
  content?: string; // Preferred field for text content
  text?: string;    // Fallback text content
  body?: string;    // Another fallback
  caption?: string; // For media
  timestamp?: string | number | Date; // Timestamp from API
  createdAt?: string | number | Date; // Alternative timestamp
  t?: string | number | Date;         // Short alternative for timestamp
  // other fields as they come from the API
}

interface WebSocketMessage {
  id: number;
  channelId: number;
  clientId: number;
  waId: string;
  from: string;
  fromMe: boolean;
  type: string;
  content: string | null;
  link: string | null;
  mediaUrl: string | null;
  imageUrl: string | null;
  mimetype: string | null;
  duration: string | null;
  timestamp: string; // ISO string format expected from backend
  quotedMsgId?: number | null;
  status?: string | null;
  errorCode?: string | null;
  errorReason?: string | null;
}

// Helper for avatar
const getAvatarFallback = (name: string = ""): string => {
  if (!name || typeof name !== 'string') return "";
  const trimmedName = name.trim();
  if (!trimmedName) return "";

  const parts = trimmedName.split(" ").filter(part => part.length > 0);

  if (parts.length === 0) return "";

  if (parts.length === 1) {
    // One word
    if (parts[0].length === 1) {
      return parts[0][0].toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  } else {
    // Two or more words
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
};

// Helper for date formatting (basic)
const formatDate = (dateString?: string | Date) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e) { return ''; }
};

const formatTime = (dateString?: string | Date) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e) { return ''; }
};


export const useChat = () => {
  const queryClient = useQueryClient();
  const [activeContactId, setActiveContactId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Fetch User Subscriptions
  const { data: userSubscriptions, isLoading: isLoadingSubscriptions, isError: isErrorSubscriptions } = useUserSubscriptions();
  console.log({userSubscriptions});
  
  const selectContact = useCallback((contactId: string | null) => {
    setActiveContactId(contactId);
  }, []);

  const updateSearchTerm = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  // Fetch Contacts (Clients)
  const {
    data: contactsData,
    fetchNextPage: fetchNextPageContacts,
    hasNextPage: hasNextPageContacts,
    isFetchingNextPage: isFetchingNextPageContacts,
    isLoading: isLoadingContacts,
    isError: isErrorContacts,
  } = useInfiniteQuery({
    queryKey: ['contacts', searchTerm], // Include searchTerm if API supports server-side filtering
    queryFn: async ({ pageParam = 1 }) => {
      // Assuming getClients takes page and a limit. Let's use a default limit.
      const limit = 15; // Or make this configurable
      const {  clients, total } = await getClients(pageParam, limit);

      return {
        data: clients,
        nextPage: clients.length < total ? pageParam + 1 : undefined, // Check if clients.length < total based on API
        total,
        currentPage: pageParam,
      };
    },
    initialPageParam: 1, // Add initialPageParam
    getNextPageParam: (lastPage: { data: Client[]; nextPage?: number; total: number; currentPage: number }) => lastPage.nextPage,
      // refetchOnWindowFocus: false, // Optional: depending on desired behavior
    }
  );

  const getActiveChannelPlanStatus = useCallback((channelId: string | number): { isActive: boolean; isExpired: boolean; subscription?: Subscription } => {
    if (!userSubscriptions || userSubscriptions.length === 0) {
      return { isActive: false, isExpired: false, subscription: undefined };
    }

    const targetChannelIdStr = String(channelId);

    const subscription = userSubscriptions.find(
      (sub) => sub.channel && String(sub.channel.id) === targetChannelIdStr
    );

    if (!subscription) {
      return { isActive: false, isExpired: false, subscription: undefined };
    }

    const now = new Date();
    const endDate = new Date(subscription.endDate);
    const isExpired = endDate < now;

    // isActive refleja el mismo campo que ya usa el backend para permitir o
    // no el envío (whatsapp.service.ts::sendMessageToWhatsApp) — se mantiene
    // en true durante todo el período de gracia posterior al vencimiento, y
    // recién pasa a false cuando el plan queda SUSPENDED de verdad. Antes se
    // combinaba con `!isExpired` acá, lo que bloqueaba el chat apenas pasaba
    // el endDate aunque el backend siguiera aceptando mensajes.
    const isActive = subscription.active;

    return { isActive, isExpired, subscription };
  }, [userSubscriptions]);

  const contacts: Contact[] = useMemo(() => {
    if (!contactsData?.pages) return [];
    return contactsData.pages.flatMap((page: { data: Client[], currentPage: number, nextPage?: number, total: number }) =>
      page.data.map((client: Client) => ({
        
        id: String(client.id), // Ensure ID is string
        channelId: client.channelId,
        name: client.name || "Unknown Contact",
        lastMessage: client.lastMessage || "No recent messages",
        timestamp: formatDate(client.updatedAt),
        unreadCount: 0, // Default as per requirement
        avatar: getAvatarFallback(client.name),
        isOnline: false, // Default as per requirement
        phoneNumber: client.phoneNumber || '',
        channelPlanStatus: getActiveChannelPlanStatus(client.channelId), // Add this line
      }))
    );
  }, [contactsData, getActiveChannelPlanStatus]);

  // Filter contacts locally if API doesn't support search for this query
  // This is a basic client-side search. For large datasets, server-side search is better.
  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;
    return contacts.filter(contact =>
      contact.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [contacts, searchTerm]);

  const deriveCursor = (lastPage: any) => {
  if (lastPage?.nextCursor) return lastPage.nextCursor; // ya viene del MS

  const list = lastPage?.messages ?? lastPage?.data ?? [];
  if (!list.length) return undefined;

  // último de la página (con orderBy id desc en el back, es el id más chico de esa página)
  const oldest = list[list.length - 1];
  return { beforeId: Number(oldest.id) };
};




  // Fetch Messages for activeContactId
  const {
    data: messagesData,
    fetchNextPage: fetchNextPageMessages,
    hasNextPage: hasNextPageMessages,
    isFetchingNextPage: isFetchingNextPageMessages,
    isLoading: isLoadingMessages,
    isError: isErrorMessages,
  } = useInfiniteQuery({
    // queryKey: ['messages', activeContactId],
    // queryFn: async ({ pageParam = 1 }) => {
    //   if (!activeContactId) return { data: [], nextPage: undefined, total: 0, currentPage: pageParam }; // Return structure consistent with successful fetch
    //   const limit = 20; // Or make this configurable
    //   const { messages: apiMessages, total } = await getMessages(activeContactId, pageParam, limit);
      
    //   return {
    //     data: apiMessages, // These are of the type from the service, let's say 'ServiceMessage'
    //     nextPage: apiMessages.length < total ? pageParam + 1 : undefined, // Check if apiMessages.length < total
    //     total,
    //     currentPage: pageParam,
    //   };
    // },
    // initialPageParam: 1, // Add initialPageParam
    // enabled: !!activeContactId, // Only run query if activeContactId is selected
    // getNextPageParam: (lastPage: { data: ApiMessage[]; nextPage?: number; total: number; currentPage: number }) => lastPage.nextPage,
    //   // refetchOnWindowFocus: false, // Optional
    queryKey: ['messages', activeContactId ?? 'none'],
  initialPageParam: null as null | { beforeTimestamp: string; beforeId: number },
  enabled: !!activeContactId,
  queryFn: ({ pageParam }) =>
  getMessages(activeContactId!, { limit: 20, ...(pageParam ?? {}) }),
  getNextPageParam: (lastPage) => deriveCursor(lastPage),
  select: (data) => {
    // dedup defensivo por id
    const seen = new Set<string>();
    const pages = data.pages.map(p => ({
      ...p,
      messages: p.messages.filter((m: any) => {
        const id = String(m.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
    }));
    return { ...data, pages };
  },
    }
  );
console.log("getMessages", messagesData);
const messages: Message[] = useMemo(() => {
  if (!messagesData?.pages) return [];

  const list = messagesData.pages.flatMap((page: any) =>
    (page.messages ?? page.data ?? []).map((msg: ApiMessage & { deleted?: boolean }) => {
      const rawTs: any = msg.timestamp ?? msg.createdAt ?? (msg as any).t;
      const tsMs = typeof rawTs === 'number' ? rawTs : Date.parse(rawTs);

      let content = '';
      if (msg.type === 'chat' || msg.type === 'text') content = msg.content || msg.text || msg.body || '';
      else if (msg.caption) content = msg.caption;
      else content = msg.content || msg.text || msg.body || '';

      return {
        id: String(msg.id),
        contactId: activeContactId!,
        content,
        timestamp: formatTime(rawTs),
        rawTimestamp: rawTs, // Keep original timestamp for date grouping
        isSent: msg.fromMe === true,
        isRead: !(msg.fromMe === true),
        messageType: (msg.type === 'chat' || msg.type === 'text') ? 'text' : msg.type,
        deleted: msg.deleted === true, // Map deleted field from backend
        _tsMs: tsMs, // auxiliar para ordenar
      } as any;
    })
  );

  list.sort((a: any, b: any) => {
  const an = Number(a.id);
  const bn = Number(b.id);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn; // numérico ASC
  // fallback si no son numéricos (ej. '3EB0...')
  return String(a.id).localeCompare(String(b.id), undefined, { numeric: true, sensitivity: 'base' });
});


  return list;
}, [messagesData, activeContactId]);




  // const messages: Message[] = useMemo(() => {
  //   if (!messagesData?.pages) return [];
  //   return messagesData.pages.flatMap((page: { data: ApiMessage[], currentPage: number, nextPage?: number, total: number }) =>
  //     page.data.map((msg: ApiMessage) => {
  //       let messageContent = "";
  //       if (msg.type === 'chat' || msg.type === 'text') {
  //         messageContent = msg.content || msg.text || msg.body || "";
  //       } else if (msg.caption) { // For media messages that might have captions
  //         messageContent = msg.caption;
  //       } else {
  //         messageContent = msg.content || msg.text || msg.body || "";
  //       }

  //       const isSentByMe = msg.fromMe === true;
  //       let timestampValue = msg.timestamp || msg.createdAt || msg.t;
  //       if (typeof timestampValue === 'number') {
  //         timestampValue = new Date(timestampValue);
  //       }

  //       return {
  //         id: String(msg.id), // Ensure ID is string
  //         contactId: activeContactId!,
  //         content: messageContent,
  //         timestamp: formatTime(timestampValue),
  //         isSent: isSentByMe,
  //         isRead: !isSentByMe, // If not sent by me, it's received and considered read by me.
  //                              // Read status of messages sent by me is more complex (e.g. needs server ack)
  //         messageType: (msg.type === 'chat' || msg.type === 'text') ? 'text' : msg.type,
  //       };
  //     })
  //   ).reverse();
  // }, [messagesData, activeContactId]);


  const sendMessage = useCallback(async (channelId: string, content: string, mediaFile?: File, quotedMsgId?: string) => {
    if (!activeContactId) {
      console.error("No active contact selected or details missing for sending message.");
      return;
    }

    try {
      const response = await sendMessageApi(channelId, activeContactId, content, mediaFile, quotedMsgId);

      if (response.success) {
        await queryClient.invalidateQueries({ queryKey: ['messages', activeContactId] });
        await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      } else {
        // Handle API error response
        console.error("Failed to send message:", response.error || "Unknown API error");
        // Optionally, display response.error to the user
      }
    } catch (error) { // This catch is for network errors or issues in sendMessageApi before returning a structured response
      console.error("Failed to send message due to an unexpected error:", error);
      // Optionally, display a generic error message to the user
    }
  }, [activeContactId, queryClient]);

  const activeContact = useMemo(() => {
    // Ensure contacts is not undefined before finding
    if (!contacts) return undefined;
    // console.log({contacts}) // Reduced logging for brevity
    return contacts.find(c => c.id === activeContactId);
  }, [contacts, activeContactId]);

  useEffect(() => {
    const handleNewMessage = (newMessage: WebSocketMessage) => {
      // Transform WebSocketMessage to the Message type used in UI if necessary
      // For now, assuming WebSocketMessage can be directly used or is compatible enough
      // The main task is to update the query cache.
      const targetContactId = String(newMessage.clientId); // Assuming clientId corresponds to contactId in UI

      queryClient.setQueryData(['messages', targetContactId], (oldData: any) => {
        if (!oldData || !oldData.pages || oldData.pages.length === 0) {
          // If no existing messages, create the structure
          return {
            pages: [{ messages: [newMessage], currentPage: 1, total: 1, nextPage: undefined }],
            pageParams: [1] // Ensure pageParams is also initialized
          };
        }
        
        // Create a deep copy to avoid mutating cache directly
        const newData = JSON.parse(JSON.stringify(oldData));
        console.log("oldData", oldData);
        console.log("newData", newData);
        // Ensure the first page and its data array exist
        if (!newData.pages[0]) {
           newData.pages[0] = { data: [], currentPage: 1, total: 0, nextPage: undefined };
        }
        // Prepend the new message to the beginning of the first page's data
        newData.pages[0].messages = [newMessage, ...newData.pages[0].messages];

        // Update total count if it exists
        if (newData.pages[0].total !== undefined) {
          newData.pages[0].total += 1;
        }

        return newData;
      });

      // Potentially invalidate contacts to update last message or unread counts
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    };

    subscribeToEvent<WebSocketMessage>('new_message_received', handleNewMessage);

    return () => {
      unsubscribeFromEvent('new_message_received', handleNewMessage);
    };
  }, [queryClient]); // activeContactId removed as per review, new messages are handled for any contact.

  useEffect(() => {
    const handleNewContact = (newContactData: Client) => {

      queryClient.setQueryData(['contacts', searchTerm], (oldData: any) => {
        if (!oldData || !oldData.pages || oldData.pages.length === 0) {
          return {
            pages: [{ data: [newContactData], total: 1, currentPage: 1, nextPage: undefined }],
            pageParams: [1],
          };
        }

        // Create a deep copy to avoid mutating cache directly
        const newData = JSON.parse(JSON.stringify(oldData));

        // Ensure the first page and its data array exist
        if (!newData.pages[0]) {
           newData.pages[0] = { data: [], total: 0, currentPage: 1 }; // Or derive from existing structure
        }

        // Add the new Client object to the beginning of the first page's data array
        newData.pages[0].data.unshift(newContactData);

        // Update total count for the query (assuming total is on the first page object from API response)
        if (newData.pages[0].total !== undefined) {
          newData.pages[0].total += 1;
        }

        return newData;
      });

      // Note: The transformation from Client to Contact (UI type) is handled by the useMemo for 'contacts'.
      // If the new contact should immediately appear with an unread message badge,
      // the logic within the 'contacts' useMemo or this handler might need adjustment,
      // or a subsequent 'new_message_received' event should handle the unread status.
    };

    // Subscribe to the 'newContact' event.
    subscribeToEvent<Client>('newContact', handleNewContact);

    return () => {
      unsubscribeFromEvent('newContact', handleNewContact);
    };
  }, [queryClient, searchTerm]);


  return {
    contacts: filteredContacts,
    fetchNextPageContacts,
    hasNextPageContacts,
    isFetchingNextPageContacts,
    isLoadingContacts,
    isErrorContacts,

    messages,
    fetchNextPageMessages,
    hasNextPageMessages,
    isFetchingNextPageMessages,
    isLoadingMessages,
    isErrorMessages,

    activeContact,
    activeContactId,
    searchTerm,
    selectContact,
    sendMessage,
    updateSearchTerm,

    // Channel Plan Status
    getActiveChannelPlanStatus,
    isLoadingSubscriptions,
    isErrorSubscriptions,
  };
};
